import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  Text,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Badge,
  Card,
  Button,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { BiArrowBack } from "react-icons/bi";
import CustomButton from "../../components/CustomButton";
import { fetchSecurityDepositDetails } from "../../redux/SecurityDepositSlice";

const SecurityDetail = () => {
  const { state } = useLocation();
  const customerId = state?.customerId;
  const orderid = state?.orderid;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { customerDetails, detailsLoading } = useSelector(
    (state) => state.sd_deposit
  );

  const [selectedPayment, setSelectedPayment] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => {
    if (customerId && orderid) {
      dispatch(fetchSecurityDepositDetails({ userid: customerId, orderid }));
    }
  }, [dispatch, customerId, orderid]);

  const handleGoBack = () => {
    navigate(-1);
  };

  // ✅ Status color logic
  const getStatusProps = (status) => {
    const normalized = status?.toString().trim().toLowerCase();
    if (normalized === "processing") {
      return { colorScheme: "orange", label: "Processing" };
    }
    if (normalized === "completed") {
      return { colorScheme: "green", label: "Completed" };
    }
    return { colorScheme: "gray", label: "N/A" };
  };

  // ✅ Handle "View" button click
  const handleViewDetails = (order) => {
    if (order.paymentmode?.toUpperCase() === "ONLINE") {
      setSelectedPayment(order);
      onOpen();
    }
  };

  return (
    <>
      {/* Header */}
      <Card
        bg="#fff"
        rounded="md"
        display="flex"
        flexDirection="row"
        alignItems="center"
        justify="space-between"
        paddingInline={5}
        paddingBlock={2}
        fontWeight="bold"
        fontSize={{ base: "md", md: "lg" }}
        mb={4}
      >
        <Text>Security Deposit Details</Text>
        <CustomButton
          icon={<BiArrowBack color="#fff" />}
          title="Back"
          height="30px"
          b_color="#000"
          onClick={handleGoBack}
        />
      </Card>

      {/* Loader */}
      {detailsLoading && (
        <Flex justify="center" align="center" height="200px">
          <Spinner size="lg" color="green.500" />
        </Flex>
      )}

      {/* Table */}
      {!detailsLoading && customerDetails?.data?.length > 0 && (
        <TableContainer
          border="1px solid"
          borderColor="gray.200"
          rounded="md"
          shadow="sm"
        >
          <Table variant="simple" size="sm">
            <Thead bg="gray.100">
              <Tr>
                <Th>Order ID</Th>
                <Th>Product Name</Th>
                <Th>Delivered Date</Th>
                <Th>SD Quantity</Th>
                <Th>SD Claimed Quantity</Th>
                <Th>Amount Received</Th>
                <Th>Amount Paid</Th>
                <Th>Payment Mode</Th>
                <Th>Status</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>

            <Tbody>
  {customerDetails.data.map((order) =>
    order.details?.map((item) => {
      const { colorScheme, label } = getStatusProps(
        item.refund_request_status
      );

      return (
        <Tr key={`${order.orderid}-${item.id}`}>
          <Td>{order.orderid}</Td>
          <Td>{item.product_name || "-"}</Td>
          <Td>
            {item.deliver_datetime &&
            item.deliver_datetime !== "0000-00-00 00:00:00"
              ? new Date(item.deliver_datetime).toLocaleDateString()
              : "-"}
          </Td>
          <Td>{item.sd_count || 0}</Td>
          <Td>{item.claimed_sd_quantity || 0}</Td>
          <Td>₹{parseFloat(item.amount_received || 0).toFixed(2)}</Td>
          <Td>₹{parseFloat(item.amount_paid || 0).toFixed(2)}</Td>

          {/* ✅ Payment Mode */}
          <Td>
            {order.paymentmode ? (
              <Badge
                colorScheme={
                  order.paymentmode.toUpperCase() === "COD"
                    ? "orange"
                    : order.paymentmode.toUpperCase() === "ONLINE"
                    ? "green"
                    : "gray"
                }
                variant="solid"
                py={1}
                px={2}
                borderRadius="md"
                fontSize="0.75rem"
              >
                {order.paymentmode.toUpperCase()}
              </Badge>
            ) : (
              "-"
            )}
          </Td>

          {/* ✅ Status */}
          <Td>
            <Badge
              colorScheme={colorScheme}
              variant="solid"
              py={1}
              px={2}
              borderRadius="md"
              fontSize="0.75rem"
            >
              {label}
            </Badge>
          </Td>

          {/* ✅ Actions */}
          <Td>
            {order.paymentmode?.toUpperCase() === "ONLINE" ? (
              <Button
                size="xs"
                colorScheme="blue"
                onClick={() => handleViewDetails(order)}
              >
                View
              </Button>
            ) : (
              <Text fontSize="xs" color="gray.500">
                N/A
              </Text>
            )}
          </Td>
        </Tr>
      );
    })
  )}
</Tbody>

          </Table>
        </TableContainer>
      )}

      {/* No Data Message */}
      {!detailsLoading &&
        (!customerDetails?.data || customerDetails.data.length === 0) && (
          <Box textAlign="center" py={10}>
            <Text fontSize="lg" color="gray.500">
              No order details found for this customer.
            </Text>
          </Box>
        )}

      {/* ✅ Small Modal for Online Payment Details */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="sm">
        <ModalOverlay />
        <ModalContent borderRadius="lg" p={3}>
          <ModalHeader fontSize="lg" textAlign="center">
            Payment Details
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={5}>
            {selectedPayment ? (
              <Box fontSize="sm" color="gray.700" lineHeight="6">
                <Flex justify="space-between">
                  <Text fontWeight="medium">Online Paid Amount:</Text>
                  <Text>₹{selectedPayment.online_amount || "0.00"}</Text>
                </Flex>

                <Flex justify="space-between">
                  <Text fontWeight="medium">Transaction No.:</Text>
                  <Text>{selectedPayment.transaction_no || "N/A"}</Text>
                </Flex>

                <Flex justify="space-between">
                  <Text fontWeight="medium">Date & Time:</Text>
                  <Text>
                    {selectedPayment.inv_date
                      ? new Date(selectedPayment.inv_date).toLocaleString()
                      : "N/A"}
                  </Text>
                </Flex>

                <Flex justify="space-between">
                  <Text fontWeight="medium">Payment Mode:</Text>
                  <Text>{selectedPayment.transactionmode || "N/A"}</Text>
                </Flex>
              </Box>
            ) : (
              <Text textAlign="center" color="gray.500">
                No payment details available.
              </Text>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default SecurityDetail;
